/**
 * LegalPage - Rechtliche Hinweise, Impressum, Datenschutzerklärung
 * Für Babelmix Beta
 * Mit i18n-Unterstützung für UI-Elemente
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Shield, FileText, Scale, Building2, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const LegalPage = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState('impressum');

  if (!isOpen) return null;

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, icon: Icon, title, children, color = 'gray' }) => {
    const isExpanded = expandedSection === id;
    const colorClasses = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      amber: 'bg-amber-50 border-amber-200 text-amber-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      gray: 'bg-gray-50 border-gray-200 text-gray-800'
    };

    return (
      <div className="border rounded-xl overflow-hidden mb-4">
        <button
          onClick={() => toggleSection(id)}
          className={`w-full flex items-center justify-between p-4 ${colorClasses[color]} border-b transition-all hover:opacity-90`}
          data-testid={`legal-section-${id}`}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5" />
            <span className="font-semibold text-lg">{title}</span>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {isExpanded && (
          <div className="p-5 bg-white">
            {children}
          </div>
        )}
      </div>
    );
  };

  const SubSection = ({ title, children }) => (
    <div className="mb-6 last:mb-0">
      <h4 className="font-semibold text-gray-800 mb-2 pb-2 border-b border-gray-100">{title}</h4>
      <div className="text-gray-600 text-sm leading-relaxed">{children}</div>
    </div>
  );

  const BulletList = ({ items }) => (
    <ul className="space-y-1.5 mt-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="text-gray-400 mt-1">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="relative bg-gray-50 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="legal-page-modal"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Scale className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-bold">{t('legal.title', 'Rechtliche Hinweise')}</h2>
                <p className="text-gray-300 text-sm">{t('legal.subtitle', 'BABELMIX (BETA)')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-white/10 hover:bg-white/20 rounded-full p-2 transition-all"
              data-testid="legal-close-button"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          
          {/* 1. Impressum */}
          <Section id="impressum" icon={Building2} title={t('legal.impressum.title', '1. Impressum')} color="blue">
            <p className="text-xs text-gray-500 mb-4">{t('legal.impressum.companyInfo', 'Angaben gemäß § 5 DDG (ehemals TMG)')}</p>
            
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="font-bold text-blue-900 text-lg mb-2">Brix Venture GmbH</div>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-center gap-2">
                  <span>{t('legal.impressum.address', 'Frickestraße 63c, 20251 Hamburg, Deutschland')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <a href="mailto:Babelmix@BrixVenture.de" className="underline hover:text-blue-600">
                    Babelmix@BrixVenture.de
                  </a>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-xs mb-1">{t('legal.impressum.managingDirector', 'Geschäftsführer')}</div>
                <div className="font-medium">Matthias Brix</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-xs mb-1">{t('legal.impressum.court', 'Registergericht')}</div>
                <div className="font-medium">{t('legal.impressum.courtInfo', 'Amtsgericht Hamburg')}</div>
                <div className="text-gray-600 text-xs">HRB 120006</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-xs mb-1">{t('legal.impressum.vatId', 'USt-IdNr.')}</div>
                <div className="font-medium">DE279095255</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-500 text-xs mb-1">{t('legal.impressum.contentResponsible', 'Inhaltlich verantwortlich (§ 18 Abs. 2 MStV)')}</div>
                <div className="font-medium">Matthias Brix</div>
              </div>
            </div>
          </Section>

          {/* 2. Nutzungsbedingungen */}
          <Section id="nutzung" icon={FileText} title={t('legal.terms.title', '2. Nutzungsbedingungen (Beta)')} color="amber">
            <SubSection title={t('legal.terms.subject.title', '2.1 Gegenstand')}>
              <p>{t('legal.terms.subject.text1', 'Babelmix ist eine digitale Kommunikationsplattform der Brix Venture GmbH.')}</p>
              <p className="mt-2">{t('legal.terms.subject.text2', 'Die Plattform befindet sich in einer geschlossenen Beta-Testphase.')}</p>
              <p className="mt-2 text-amber-700 font-medium">{t('legal.terms.subject.text3', 'Die Nutzung erfolgt ausschließlich auf Einladung und unentgeltlich.')}</p>
            </SubSection>

            <SubSection title={t('legal.terms.betaStatus.title', '2.2 Beta-Status')}>
              <p>{t('legal.terms.betaStatus.text', 'Die Plattform dient ausschließlich Testzwecken. Funktionen können unvollständig, fehlerhaft oder vorübergehend nicht verfügbar sein.')}</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <div className="font-medium text-amber-800 mb-2">{t('legal.terms.betaStatus.noClaimTitle', 'Es besteht kein Anspruch auf:')}</div>
                <BulletList items={[
                  t('legal.terms.betaStatus.noClaim1', 'dauerhafte Verfügbarkeit'),
                  t('legal.terms.betaStatus.noClaim2', 'bestimmte Funktionalitäten'),
                  t('legal.terms.betaStatus.noClaim3', 'Fehlerfreiheit'),
                  t('legal.terms.betaStatus.noClaim4', 'Speicherung oder Wiederherstellung von Daten')
                ]} />
              </div>
              <p className="mt-3 text-gray-500 text-xs">{t('legal.terms.betaStatus.operatorRights', 'Der Betreiber behält sich vor, Funktionen jederzeit zu ändern oder einzustellen.')}</p>
            </SubSection>

            <SubSection title={t('legal.terms.selfResponsibility.title', '2.3 Eigenverantwortliche Nutzung')}>
              <p>{t('legal.terms.selfResponsibility.text1', 'Die Nutzung erfolgt eigenverantwortlich.')}</p>
              <p className="mt-2">{t('legal.terms.selfResponsibility.text2', 'Die Plattform ist nicht zur Speicherung besonders sensibler personenbezogener Daten, Geschäftsgeheimnisse oder sicherheitskritischer Informationen bestimmt.')}</p>
              <p className="mt-2">{t('legal.terms.selfResponsibility.text3', 'Nutzer sind für die von ihnen übermittelten Inhalte selbst verantwortlich. Eine inhaltliche Prüfung durch den Betreiber erfolgt nicht.')}</p>
            </SubSection>

            <SubSection title={t('legal.terms.liability.title', '2.4 Haftung')}>
              <p>{t('legal.terms.liability.text', 'Die Haftung der Brix Venture GmbH ist ausgeschlossen, außer:')}</p>
              <BulletList items={[
                t('legal.terms.liability.except1', 'bei Vorsatz'),
                t('legal.terms.liability.except2', 'bei grober Fahrlässigkeit'),
                t('legal.terms.liability.except3', 'bei Verletzung von Leben, Körper oder Gesundheit')
              ]} />
              <p className="mt-3 text-sm">{t('legal.terms.liability.cardinalText', 'Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) ist die Haftung auf den vorhersehbaren, typischerweise eintretenden Schaden begrenzt.')}</p>
              <p className="mt-2 text-sm text-gray-500">{t('legal.terms.liability.gratuitousText', 'Da die Nutzung unentgeltlich erfolgt, ist die Haftung zusätzlich auf vorsätzliches und grob fahrlässiges Verhalten beschränkt. Eine weitergehende Haftung ist ausgeschlossen.')}</p>
            </SubSection>

            <SubSection title={t('legal.terms.misuse.title', '2.5 Missbrauch')}>
              <p>{t('legal.terms.misuse.text1', 'Die Nutzung zu rechtswidrigen Zwecken ist untersagt.')}</p>
              <p className="mt-2">{t('legal.terms.misuse.text2', 'Der Betreiber ist berechtigt, Zugänge bei missbräuchlicher Nutzung zu sperren.')}</p>
            </SubSection>

            <SubSection title={t('legal.terms.jurisdiction.title', '2.6 Gerichtsstand')}>
              <p>{t('legal.terms.jurisdiction.text', 'Gerichtsstand ist – soweit gesetzlich zulässig – Hamburg.')}</p>
            </SubSection>
          </Section>

          {/* 3. Datenschutzerklärung */}
          <Section id="datenschutz" icon={Shield} title={t('legal.privacy.title', '3. Datenschutzerklärung')} color="green">
            <SubSection title={t('legal.privacy.controller.title', '3.1 Verantwortlicher')}>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="font-medium">Brix Venture GmbH</div>
                <div className="text-sm">Frickestraße 63c, 20251 Hamburg</div>
                <div className="text-sm">
                  <a href="mailto:Babelmix@BrixVenture.de" className="text-green-700 underline">
                    Babelmix@BrixVenture.de
                  </a>
                </div>
              </div>
            </SubSection>

            <SubSection title={t('legal.privacy.hosting.title', '3.2 Hosting und Infrastruktur')}>
              <p>{t('legal.privacy.hosting.text', 'Die technische Bereitstellung erfolgt innerhalb der Europäischen Union.')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-gray-500">{t('legal.privacy.hosting.hosting', 'Hosting')}</div>
                  <div className="font-medium text-sm">Emergent Labs</div>
                  <div className="text-xs text-green-600">EU – Frankfurt</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-gray-500">{t('legal.privacy.hosting.database', 'Datenbank')}</div>
                  <div className="font-medium text-sm">MongoDB Atlas</div>
                  <div className="text-xs text-green-600">EU</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-gray-500">{t('legal.privacy.hosting.fileStorage', 'Dateispeicher')}</div>
                  <div className="font-medium text-sm">Cloudflare R2</div>
                  <div className="text-xs text-green-600">EU</div>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">{t('legal.privacy.hosting.legalBasis', 'Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO')}</p>
            </SubSection>

            <SubSection title={t('legal.privacy.processedData.title', '3.3 Verarbeitete Daten')}>
              <div className="space-y-3">
                <div className="border rounded-lg p-3">
                  <div className="font-medium text-gray-800 mb-2">{t('legal.privacy.processedData.accountData', 'Kontodaten')}</div>
                  <div className="text-sm">{t('legal.privacy.processedData.accountDataDesc', 'Name, Telefonnummer, Firma (optional), Sprache, Geräte-Token')}</div>
                  <div className="text-xs text-gray-500 mt-1">{t('legal.privacy.processedData.accountDataRetention', 'Speicherdauer: bis zur Accountlöschung')}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="font-medium text-gray-800 mb-2">{t('legal.privacy.processedData.communicationData', 'Kommunikationsdaten')}</div>
                  <div className="text-sm">{t('legal.privacy.processedData.communicationDataDesc', 'Textnachrichten, Bilder, Sprachnachrichten, Dateien, Projekte, Aufgaben')}</div>
                  <div className="text-xs text-gray-500 mt-1">{t('legal.privacy.processedData.communicationDataRetention', 'Speicherung projektbezogen bis zur Löschung')}</div>
                </div>
              </div>
            </SubSection>

            <SubSection title={t('legal.privacy.invitations.title', '3.4 Einladungen')}>
              <p>{t('legal.privacy.invitations.text', 'Bestehende Nutzer können andere Personen über deren Telefonnummer zur Nutzung von Babelmix einladen.')}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3">
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>{t('legal.privacy.invitations.processing', 'Die eingegebene Telefonnummer wird ausschließlich zum Versand einer einmaligen Einladung verarbeitet.')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    <span>{t('legal.privacy.invitations.deletion', 'Erfolgt keine Registrierung, wird die Telefonnummer nach spätestens 14 Tagen automatisch gelöscht.')}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">{t('legal.privacy.invitations.legalBasis', 'Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Einladung weiterer Projektmitglieder)')}</p>
            </SubSection>

            <SubSection title={t('legal.privacy.authentication.title', '3.5 Authentifizierung')}>
              <p>{t('legal.privacy.authentication.text', 'Login erfolgt über:')}</p>
              <BulletList items={[
                t('legal.privacy.authentication.method1', 'SMS-Einmalpasswort (OTP)'),
                t('legal.privacy.authentication.method2', 'optionaler PIN')
              ]} />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                <div className="text-sm">
                  <strong>{t('legal.privacy.authentication.smsProvider', 'SMS-Versand über: Twilio Inc., USA')}</strong>
                </div>
                <div className="text-xs text-blue-700 mt-1">{t('legal.privacy.authentication.twilioCertified', 'Twilio ist nach dem EU-US Data Privacy Framework zertifiziert.')}</div>
              </div>
            </SubSection>

            <SubSection title={t('legal.privacy.transcription.title', '3.6 KI-gestützte Transkription')}>
              <p>{t('legal.privacy.transcription.text', 'Sprachnachrichten werden zur Transkription übermittelt an:')}</p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2">
                <div className="font-medium text-purple-800">OpenAI, Inc., USA</div>
                <div className="text-sm text-purple-700">Whisper-Technologie</div>
                <div className="text-xs text-purple-600 mt-1">{t('legal.privacy.transcription.openaiCertified', 'OpenAI ist nach dem EU-US Data Privacy Framework zertifiziert.')}</div>
              </div>
              <p className="mt-2 text-xs text-gray-500">{t('legal.privacy.transcription.legalBasis', 'Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO | Drittlandübermittlung gemäß Art. 45 DSGVO')}</p>
            </SubSection>

            <SubSection title={t('legal.privacy.thirdCountry.title', '3.7 Drittlandübermittlung')}>
              <p>{t('legal.privacy.thirdCountry.text', 'Datenübermittlung in die USA erfolgt ausschließlich im Rahmen zertifizierter Anbieter nach dem EU-US Data Privacy Framework.')}</p>
            </SubSection>

            <SubSection title={t('legal.privacy.cookies.title', '3.8 Cookies und Tracking')}>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                  <span>✓</span> {t('legal.privacy.cookies.noTracking', 'Keine Trackingtools (z.B. Google Analytics)')}
                </div>
                <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                  <span>✓</span> {t('legal.privacy.cookies.noCookies', 'Keine HTTP-Cookies')}
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  <strong>LocalStorage</strong> {t('legal.privacy.cookies.localStorage', 'wird verwendet für: Spracheinstellungen, Installationsstatus, Onboarding-Status')}
                </div>
                <div className="text-xs text-gray-500 mt-1">{t('legal.privacy.cookies.noProfiling', 'Dabei erfolgt keine Profilbildung.')}</div>
              </div>
            </SubSection>

            <SubSection title={t('legal.privacy.retention.title', '3.9 Speicherdauer')}>
              <p>{t('legal.privacy.retention.text', 'Personenbezogene Daten werden nur so lange gespeichert, wie dies für die Bereitstellung der Plattform erforderlich ist.')}</p>
            </SubSection>

            <SubSection title={t('legal.privacy.rights.title', '3.10 Betroffenenrechte')}>
              <p>{t('legal.privacy.rights.text', 'Nutzer haben das Recht auf:')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {[
                  t('legal.privacy.rights.right1', 'Auskunft'),
                  t('legal.privacy.rights.right2', 'Berichtigung'),
                  t('legal.privacy.rights.right3', 'Löschung'),
                  t('legal.privacy.rights.right4', 'Einschränkung'),
                  t('legal.privacy.rights.right5', 'Datenübertragbarkeit'),
                  t('legal.privacy.rights.right6', 'Widerspruch')
                ].map((right) => (
                  <div key={right} className="bg-gray-50 rounded px-2 py-1 text-sm text-center">
                    {right}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-sm">
                <strong>{t('legal.privacy.rights.complaint', 'Beschwerderecht bei:')}</strong><br />
                {t('legal.privacy.rights.authority', 'Der Hamburgische Beauftragte für Datenschutz und Informationsfreiheit')}
              </div>
            </SubSection>
          </Section>

          {/* Beta-Hinweis Footer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div>
                <div className="font-semibold text-amber-800 mb-1">{t('legal.betaNotice.title', 'Beta-Hinweis')}</div>
                <p className="text-sm text-amber-700">{t('legal.betaNotice.text', 'Babelmix befindet sich in einer geschlossenen Beta-Testphase. Die Nutzung erfolgt unentgeltlich und zu Testzwecken. Funktionen können eingeschränkt oder fehlerhaft sein.')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 bg-white border-t">
          <Button
            onClick={onClose}
            className="w-full bg-gray-800 hover:bg-gray-900 text-white"
            data-testid="legal-understood-button"
          >
            {t('legal.understood', 'Verstanden')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
